import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { Carousel, Container, Column, Row, List, ReviewCard, Icon } from '@pella/sash-framework';
import { Layout, SEO, CTA, Image, Video } from '../../components';
import { isValidArray } from '../../components/utilities/validations';

import HomepageHero from '../../components/template/homepage/homepage-hero';
import ArticleCard from '../../components/common/article-card';
import DynamicComponent from '../../components/common/dynamic-component';
import CardSection from '../../components/common/card-section';
import ShopLearnPanel from '../../components/template/homepage/shop-learn-panel';
import ShopTheLook from '../../components/template/homepage/shop-the-look';
import RTAShowroomPanel from '../../components/template/homepage/rta-showroom-panel';
import { HoverEmbed } from '../../components/third-party/hover';
import AnniversaryBanner from '../../components/common/anniversary-banner';

import './index.css';

const Homepage = (props) => {
	const pageData = props.data.contentstackHome2023;
	const globalConfig = props.data.contentstackGlobalConfig;
	const socialsArray = props.data.socials?.social?.ctas?.map((cta) => JSON.stringify(cta.link?.href))?.join(', ');
	const [toggles, setToggles] = useState({ inspiration: 0, product: 0 });

	const onToggleClick = (state, value) => setToggles({ ...toggles, [state]: value });

	return (
		<Layout
			showToggle
			id='template-Homepage'
		>
			{pageData.hero && <HomepageHero data={pageData.hero} />}

			{pageData.hero.banner?.heading && <AnniversaryBanner data={pageData.hero.banner} />}

			{pageData.hero.claims?.[0] && (
				<Container>
					<Carousel
						wrapperClassName='claimsSlider'
						slidesToShow={{ xs: 1, md: 3 }}
						arrowsClassName='[&_button]:bottom-2.5 [&_button]:top-2.5 [&_button]:!mt-0 [&_button]:!translate-y-0 [&_button]:h-auto md:hidden'
						dotsClassName='!mt-0 md:hidden'
						showArrows
						showDots
					>
						{pageData.hero.claims.map((claim, index) => (
							<div
								className={`claimsSlide relative pt-5 text-center max-md:px-4 md:py-5 lg:px-5 ${index === 0 ? '!pl-0' : index === pageData.hero.claims.length - 1 ? '!pr-0' : ''}`}
								key={index}
							>
								{claim.heading && (
									<div
										className='claimsSlide-heading whitespace-nowrap text-lg font-bold tracking-wider text-black xl:text-22px [&>p]:leading-10 [&_img]:block [&_img]:h-6 [&_img]:w-6 xl:[&_img]:h-7 xl:[&_img]:w-7 [&_p]:inline-flex [&_p]:min-h-[36px] [&_p]:items-center [&_p]:bg-primary [&_p]:px-1 [&_span]:mr-2 [&_span]:flex-none [&_span]:text-28px [&_span]:lg:mr-2.5 xl:[&_span]:text-32px'
										dangerouslySetInnerHTML={{ __html: claim.heading }}
									/>
								)}
								{claim.claim && (
									<div
										className='claimsSlide-claim text-body-2 pt-1.5 [&_a]:relative [&_a]:z-2'
										dangerouslySetInnerHTML={{ __html: claim.claim }}
									/>
								)}
								{claim.url && (
									<Link
										to={claim.url}
										aria-label={claim.heading}
										className='claimsSlide-link absolute left-3 right-3 top-0 h-full'
									/>
								)}
							</div>
						))}
					</Carousel>
				</Container>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.schedule_consultation != null) {
						return (
							<Container
								className='blockRta my-8 max-md:!px-0 md:flex md:items-center md:justify-between'
								key={index}
							>
								<Image
									className='blockRta-image md:w-5/12'
									data={block.schedule_consultation.image[0]}
								/>
								<div className='max-md:px-6 max-md:pt-4 md:w-6/12'>
									<h2 className='blockRta-heading text-h3 mb-3'>{block.schedule_consultation.heading}</h2>
									<p className='blockRta-badge text-subtitle-2 mb-3 flex w-fit items-center bg-gray-10 px-[14px] py-1 text-gray-70'>
										<Icon
											name='alarm'
											className='mr-1'
											size={17}
										/>
										{block.schedule_consultation.badge}
									</p>
									<p className='blockRta-subheading mb-6 mt-0 text-black'>{block.schedule_consultation.subheading}</p>
									<div
										className={`blockRta-description list-checks text-subtitle-2 !mb-6 text-black [&_li]:mb-4 [&_ul]:!m-0 [&_ul]:!pl-1`}
										dangerouslySetInnerHTML={{ __html: block.schedule_consultation.description }}
									/>
									<CTA
										color='primary-alt'
										data={block.schedule_consultation.cta[0]}
										className='blockRta-cta'
									/>
								</div>
							</Container>
						);
					}

					if (isValidArray(block.feature?.feature)) {
						return (
							<div
								className='blockFeature home-feature'
								key={index}
							>
								<Container noPadding>
									<Row
										noGutters={true}
										reverse={block.feature.feature[0].image_position === 'right'}
									>
										<Column
											xs={12}
											md={7}
											xl={6}
										>
											{block.feature.feature[0].image?.[0] && !block.feature.feature[0].image[0].video?.video_id && (
												<div className='md:h-full'>
													<Image
														className='blockFeature-image w-full md:h-full'
														data={block.feature.feature[0].image[0]}
													/>
												</div>
											)}
											{block.feature.feature[0].image?.[0]?.video?.video_id && (
												<div>
													<Video
														className='blockFeature-video w-full'
														data={block.feature.feature[0].image[0]}
													/>
												</div>
											)}
										</Column>

										<Column
											xs={12}
											md='fluid'
											key={index}
										>
											<div className={`flex h-full flex-col items-start justify-center px-6 py-5 md:px-8 md:py-10 xl:px-16`}>
												{block.feature.feature[0].icon?.[0].url && (
													<Image
														data={{ __html: block.feature.feature[0].icon }}
														className='blockFeature-icon'
													/>
												)}
												{block.feature.feature[0].label_heading && (
													<div
														className='blockFeature-label text-overline flex items-center [&_span]:pr-2 [&_span]:text-primary'
														dangerouslySetInnerHTML={{ __html: block.feature.feature[0].label_heading }}
													/>
												)}
												{block.feature.feature[0].heading && (
													<h2
														className='blockFeature-heading !mb-0 pt-1.5'
														dangerouslySetInnerHTML={{ __html: block.feature.feature[0].heading }}
													/>
												)}
												{block.feature.feature[0].description && (
													<div
														className='blockFeature-description text-subtitle py-4 md:py-3 lg:py-5'
														dangerouslySetInnerHTML={{ __html: block.feature.feature[0].description }}
													/>
												)}
												<List bullets='none'>
													{block.feature.feature[0]?.ctas.map((link, ctaIndex) => (
														<li key={`cta-${ctaIndex}`}>
															<CTA
																color={'primary'}
																data={link}
																className={`blockFeature-cta-${ctaIndex} max-md:mb-6 max-sm:mb-5`}
															/>
														</li>
													))}
												</List>
											</div>
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.inspiration != null) {
						return (
							<div
								className='homeInspire bg-gray-5 py-6 md:py-8 xl:py-10'
								key={index}
							>
								<Container>
									<div className='homeInspire-intro relative'>
										{block.inspiration.heading && (
											<h2
												className='homeInspire-heading'
												dangerouslySetInnerHTML={{ __html: block.inspiration.heading }}
											/>
										)}

										{block.inspiration.description && (
											<div
												className='homeInspire-description text-subtitle mt-5 xl:mt-2 xl:max-w-prose'
												dangerouslySetInnerHTML={{ __html: block.inspiration.description }}
											/>
										)}

										{isValidArray(block.inspiration.toggles) && (
											<div className='homeInspire-toggle my-6 flex w-full border border-gray-40 bg-white p-1 sm:w-80 md:absolute md:right-0 md:top-0 md:my-0 xl:top-1/2 xl:-translate-y-1/2'>
												{block.inspiration.toggles.map((toggle, index) => (
													<button
														className={`homeInspire-toggleBtn flex-1 ${toggles.inspiration === index ? 'bg-black text-white' : 'text-gray-60'} py-1.5 text-center text-base font-semibold`}
														onClick={() => onToggleClick('inspiration', index)}
														key={index}
													>
														{toggle.toggle_name}
													</button>
												))}
											</div>
										)}
									</div>
								</Container>

								{isValidArray(block.inspiration.toggles) &&
									block.inspiration.toggles.map((group, index) => {
										return (
											<div
												key={index}
												className='homeInspire-group mt-6 md:mt-8 xl:mt-6'
											>
												{isValidArray(group.articles) && (
													<Container className={`${toggles.inspiration === index ? '' : 'hidden'}`}>
														<Carousel
															slidePadding={24}
															slidesToShow={{ xs: 1, xl: 2 }}
															arrowsClassName='[&_button]:border-0'
															showArrows
															showSlideCount
														>
															{group.articles.map((article, index) => (
																<ArticleCard
																	key={index}
																	data={article}
																	className='homeInspire-articleCard h-full'
																/>
															))}
														</Carousel>
													</Container>
												)}

												{isValidArray(group.dynamic) && (
													<DynamicComponent
														data={group}
														className='homeInspire-dynamic !mt-0'
														toggles={toggles}
													/>
												)}

												{isValidArray(group.cta) && (
													<div className='homeInspire-cta mt-6 text-center md:mt-8 xl:mt-6'>
														<Container className={`${toggles.inspiration === index ? '' : 'hidden'}`}>
															<CTA
																data={group.cta[0]}
																className='decoration-black'
															/>
														</Container>
													</div>
												)}
											</div>
										);
									})}
							</div>
						);
					}

					if (isValidArray(block.cards?.cards)) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className={`blockCards-${index} py-6 md:py-8 xl:py-10`}
							/>
						);
					}

					if (block.shop_and_learn != null) {
						return (
							<ShopLearnPanel
								key={index}
								data={block.shop_and_learn}
							/>
						);
					}

					if (block.ratings != null) {
						return (
							<div
								className='blockRatings bg-gray-5 py-6 md:py-8 xl:py-12'
								key={index}
							>
								<Container>
									<div className='flex flex-row flex-wrap md:items-center md:gap-6'>
										{block.ratings.heading && (
											<div className='mb-2.5 max-w-full flex-12/12 md:mb-0 md:flex-1'>
												<h2 className='blockRatings-heading mb-0 bg-primary px-2 py-1 text-center !leading-none md:inline-block'>{block.ratings.heading}</h2>
											</div>
										)}

										{isValidArray(block.ratings.cta) && (
											<div className='blockRatings-ctaWrapper order-last mt-4 max-w-full flex-12/12 md:order-none md:mt-0 md:flex-none'>
												<CTA
													data={block.ratings.cta[0]}
													size='sm'
													className='blockRatings-cta'
												/>
											</div>
										)}

										{isValidArray(block.ratings.review) && (
											<Carousel
												key={block.ratings.review.length}
												slidesToShow={{ xs: 1, md: 3 }}
												wrapperClassName='blockRatings-carousel [&>p]:!mb-0 flex-12/12 max-w-full'
												className='py-2.5 md:flex-wrap md:!gap-6 md:overflow-hidden xl:items-start md:max-xl:[&>*:last-child]:max-w-full md:max-xl:[&>*:last-child]:flex-12/12 md:[&>*]:max-w-6/12 md:[&>*]:flex-[0_0_calc(50%-12px)] xl:[&>*]:flex-1'
												slidePadding={16}
												showArrows
												showSlideCount
											>
												{block.ratings.review.map((review, ratingIndex) => (
													<ReviewCard
														date={review.date}
														rating={review.stars}
														key={`review-${ratingIndex}`}
														className={`blockRatings-review-${ratingIndex}`}
													>
														{review.description?.[0] && globalConfig?.reviews?.heading_1 && <ReviewCard.Heading content={globalConfig.reviews.heading_1} />}
														{review.description?.[0] && <ReviewCard.Content content={review.description[0]} />}
														{review.description?.[1] && globalConfig?.reviews?.heading_2 && <ReviewCard.Heading content={globalConfig.reviews.heading_2} />}
														{review.description?.[1] && <ReviewCard.Content content={review.description[1]} />}

														<ReviewCard.Author name={review.citation} />

														<Link
															to={review.tag.href}
															className='blockRatings-review-cta mt-2.5 inline-block rounded-full border border-gray-15 bg-gray-3 px-3 py-0.5 text-[10px] font-semibold !no-underline transition-colors hover:bg-black hover:text-white md:text-xs xl:mt-6'
														>
															{review.tag.title}
														</Link>
													</ReviewCard>
												))}
											</Carousel>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.shop_the_look != null) {
						return (
							<ShopTheLook
								data={block.shop_the_look}
								key={index}
							/>
						);
					}

					if (block.rta != null) {
						return (
							<RTAShowroomPanel
								data={block.rta}
								key={index}
							/>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								id={block.embed.anchor_text}
								className='my-6 md:my-8 xl:my-10'
							>
								<HoverEmbed configId={block.embed.embed_id} />
							</div>
						);
					}

					if (block.banner != null) {
						return (
							<AnniversaryBanner
								key={index}
								data={block.banner}
							/>
						);
					}
				})}

			<script
				type='application/ld+json'
				dangerouslySetInnerHTML={{
					__html: `{
						"@context": "https://schema.org",
						"@type": "Organization",
						"name": "Pella Corporation",
						"url": "https://www.pella.com",
						"logo": "https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt5d51af0d3a33260d/63615b77ff7b405f6b3f58e9/pella-logo-black-spot.png",
						"sameAs": [${socialsArray}],
						"founder": {
							"@type": "Person",
							"name": "Pete Kuyper"
						},
						"foundingDate": "1925",
						"foundingLocation": "Pella, Iowa, USA",
						"additionalProperty": [
							{
								"@type": "PropertyValue",
								"name": "Forbes Best Employer for Women 2024",
								"value": "https://pressroom.pella.com/pella-corporation-recognized-as-a-forbes-best-employer-for-women-2024/"
							},
							{
								"@type": "PropertyValue",
								"name": "Fast Company's World's Most Innovative Companies 2023",
								"value": "https://pressroom.pella.com/pella-corporation-named-a-2023-worlds-most-innovative-company-by-fast-company/"
							},
							{
								"@type": "PropertyValue",
								"name": "Best of IBS&trade; Award for Pella&reg; Steady Set&trade; Interior Installation System",
								"value": "https://pressroom.pella.com/pella-steady-set-interior-installation-system-earns-best-of-ibs-award-at-ibs-2024/"
							},
							{
								"@type": "PropertyValue",
								"name": "Forbes Best-In-State Employer 2024",
								"value": "https://pressroom.pella.com/pella-corporation-recognized-as-a-best-in-state-employer-for-the-third-consecutive-year/"
							},
							{
								"@type": "PropertyValue",
								"name": "Architizer's 2024 A+Product Awards for Hidden Screen",
								"value": "https://pressroom.pella.com/pellas-hidden-screen-wins-architizers-2024-aproduct-awards/"
							},
							{
								"@type": "PropertyValue",
								"name": "2024 Campus Forward Award by RippleMatch",
								"value": "https://pressroom.pella.com/pella-recognized-as-2024-campus-forward-award-winner-by-ripplematch/"
							},
							{
								"@type": "PropertyValue",
								"name": "America's Greatest Workplaces for Diversity 2024 by Newsweek",
								"value": "https://pressroom.pella.com/pella-corporation-named-one-of-americas-greatest-workplaces-for-diversity--for-the-second-year-in-a-row/"
							},
							{
								"@type": "PropertyValue",
								"name": "Innovation Partner of the Year 2023 by Lowe's",
								"value": "https://pressroom.pella.com/pella-recognized-as-innovation-partner-of-the-year-by-lowes/"
							},
							{
								"@type": "PropertyValue",
								"name": "2022 Good Housekeeping Home Renovation Award for Hidden Screen",
								"value": "https://pressroom.pella.com/hidden-screen-recognized-as-unique-innovation-in--2022-window--door-awards/"
							},
							{
								"@type": "PropertyValue",
								"name": "Architect's Newspaper 2022 Best of Products Award for Hidden Screen",
								"value": "https://pressroom.pella.com/hidden-screen-recognized-as-unique-innovation-in--2022-window--door-awards/"
							}
						],
						"employee": {
							"@type": "Person",
							"name": "Tim Yaggi",
							"jobTitle": "President and CEO"
						},
						"knowsAbout": [
							"Window Manufacturing",
							"Door Manufacturing",
							"Window Installation",
							"Door Installation",
							"Energy Efficiency",
							"Architectural Innovation"
						],
						"areaServed": ["US", "Canada"],
						"aggregateRating": {
							"@type": "AggregateRating",
							"ratingValue": "4.7",
							"reviewCount": "523",
							"bestRating": "5",
							"worstRating": "1"
						},
						"award": [
							"Forbes Best Employer for Women 2024",
							"Fast Company's World's Most Innovative Companies 2023",
							"Best of IBS&trade; Award for Pella&reg; Steady Set&trade; Interior Installation System",
							"Forbes Best-In-State Employer 2024",
							"Architizer's 2024 A+Product Awards for Hidden Screen",
							"2024 Campus Forward Award by RippleMatch",
							"America's Greatest Workplaces for Diversity 2024 by Newsweek",
							"Innovation Partner of the Year 2023 by Lowe's",
							"2022 Good Housekeeping Home Renovation Award for Hidden Screen",
							"Architect's Newspaper 2022 Best of Products Award for Hidden Screen"
						]
					}`,
				}}
			/>
		</Layout>
	);
};

Homepage.propTypes = {
	data: PropTypes.object.isRequired,
};

// eslint-disable-next-line react/prop-types -- gatsby head component
export const Head = ({ data }) => <SEO {...data.contentstackHome2023.seo} />;

export default Homepage;

export const query = graphql`
	query ($uid: String!) {
		contentstackHome2023(id: { eq: $uid }) {
			url
			hero {
				heading
				description
				cta {
					...CTA
				}
				file {
					url
					gatsbyImageData(quality: 90)
				}
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
				video {
					...Video
				}
				image_cta {
					...CTA
				}
				claims {
					heading
					claim
					url
				}
				banner {
					thin
					background {
						url
					}
					badge
					heading
					cta {
						...CTA
					}
				}
			}
			modular_blocks {
				embed {
					anchor_text
					embed_id
				}
				feature {
					feature {
						...Feature
					}
				}
				inspiration {
					heading
					description
					toggles {
						toggle_name
						dynamic {
							...Dynamic
						}
						articles {
							url
							top_content {
								heading
								subheading
							}
							category_cta {
								...CTA
							}
							modular_blocks {
								all_images {
									image {
										...Image
										image {
											url
											gatsbyImageData(quality: 90)
										}
									}
								}
							}
						}
						cta {
							...CTA
						}
					}
				}
				cards {
					...CardSection
				}
				shop_and_learn {
					heading
					subheading
					toggle {
						heading
						group {
							card {
								...Card
							}
							subheading
							tiles {
								...Tile
							}
						}
					}
				}
				ratings {
					heading
					cta {
						...CTA
					}
					review {
						date
						stars
						description
						citation
						tag {
							title
							href
						}
					}
				}
				shop_the_look {
					heading
					subheading
					looks {
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						tiles {
							...Tile
						}
					}
				}
				rta {
					heading
				}
				schedule_consultation {
					badge
					description
					heading
					image_position
					subheading
					cta {
						...CTA
					}
					image {
						...Image
					}
				}
				embed {
					anchor_text
					embed_id
				}
				banner {
					thin
					background {
						url
					}
					badge
					heading
					cta {
						...CTA
					}
				}
			}
			seo {
				...SEO
			}
		}
		contentstackGlobalConfig {
			reviews {
				heading_1
				heading_2
			}
		}
		socials: contentstackFooter {
			social {
				ctas {
					...CTA
				}
			}
		}
	}
`;
